import { useState } from "react";
import { motion } from "framer-motion";
import { z } from "zod";
import { Loader2, CheckCircle2, Check, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const LEVELS: string[] = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "XB", "XS", "XG", "XP", "XD", "XSA"];
const NUM_LEVELS: string[] = LEVELS.filter((l) => /^\d+$/.test(l));
const X_LEVELS: string[] = LEVELS.filter((l) => !/^\d+$/.test(l));
const labelFor = (lvl: string) => (/^\d+$/.test(lvl) ? `Lvl ${lvl}` : lvl);
const inputClass =
  "bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-primary/50 focus-visible:ring-offset-0";

const schema = z.object({
  team_name: z.string().trim().min(1, "Required").max(120),
  team_usag_no: z.string().trim().min(1, "Required").max(40),
  state: z.string().trim().min(2, "Required").max(60),
  club: z.string().trim().min(1, "Required").max(120),
  contact_name: z.string().trim().min(1, "Required").max(120),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().min(7, "Invalid phone").max(40),
  num_compulsory: z.coerce.number().int().min(0).max(999),
  num_xcel: z.coerce.number().int().min(0).max(999),
  num_optionals: z.coerce.number().int().min(0).max(999),
  levels: z.array(z.string()).min(1, "Select at least one level"),
});

const initial = {
  team_name: "",
  team_usag_no: "",
  state: "",
  club: "",
  contact_name: "",
  email: "",
  phone: "",
  num_compulsory: "0",
  num_xcel: "0",
  num_optionals: "0",
};

const PreRegistrationForm = ({ id = "pre-register-form" }: { id?: string }) => {
  const [values, setValues] = useState(initial);
  const [levels, setLevels] = useState<string[]>([]);
  const [priority2028, setPriority2028] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});


  const set = (k: keyof typeof initial) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  const toggleLevel = (lvl: string) =>
    setLevels((curr) => (curr.includes(lvl) ? curr.filter((l) => l !== lvl) : [...curr, lvl]));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse({ ...values, levels });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as string;
        if (!fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      toast({ title: "Please fix the errors", description: "Some required fields are missing or invalid.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const referenceNumber = `WC2027-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const { error } = await supabase
      .from("pre_registrations")
      .insert({
        ...parsed.data,
        reference_number: referenceNumber,
        waitlist: true,
        priority_2028: priority2028,
        status: "waitlist",
      } as never);
    setSubmitting(false);

    if (error) {
      toast({ title: "Submission failed", description: error.message, variant: "destructive" });
      return;
    }
    // Fire-and-forget confirmation email — don't block UX if it fails
    supabase.functions
      .invoke("send-transactional-email", {
        body: {
          templateName: "pre-registration-confirmation",
          recipientEmail: parsed.data.email,
          idempotencyKey: `prereg-${referenceNumber}`,
          templateData: {
            contactName: parsed.data.contact_name,
            teamName: parsed.data.team_name,
            club: parsed.data.club,
            referenceNumber,
            numCompulsory: parsed.data.num_compulsory,
            numXcel: parsed.data.num_xcel,
            numOptionals: parsed.data.num_optionals,
            levels: parsed.data.levels,
          },
        },
      })
      .catch((err) => console.error("Confirmation email failed", err));
    // Internal copy to ops inbox
    supabase.functions
      .invoke("send-transactional-email", {
        body: {
          templateName: "pre-registration-internal-copy",
          recipientEmail: "info@meetops.com",
          idempotencyKey: `prereg-internal-${referenceNumber}`,
          templateData: {
            contactName: parsed.data.contact_name,
            contactEmail: parsed.data.email,
            contactPhone: parsed.data.phone,
            teamName: parsed.data.team_name,
            club: parsed.data.club,
            referenceNumber,
            numCompulsory: parsed.data.num_compulsory,
            numXcel: parsed.data.num_xcel,
            numOptionals: parsed.data.num_optionals,
            levels: parsed.data.levels,
            notes: (parsed.data as { notes?: string }).notes,
          },
        },
      })
      .catch((err) => console.error("Internal copy email failed", err));
    setReference(referenceNumber);
    setDone(true);
    setValues(initial);
    setLevels([]);
    setPriority2028(false);
    toast({ title: "Waitlist request received", description: `Reference ${referenceNumber} — confirmation email on its way.` });
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-10 text-center max-w-2xl mx-auto"
      >
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-5" />
        <h3 className="font-display text-3xl text-white mb-3">You're on the waitlist</h3>
        <p className="text-white/60">Winner's Cup 2027 is currently full. Your team has been added to the waitlist and we'll contact you if a spot opens up.</p>
        {reference && (
          <div className="mt-6 inline-flex flex-col items-center rounded-xl border border-primary/40 bg-primary/10 px-6 py-4">
            <span className="text-[11px] tracking-[0.18em] text-primary/80 font-semibold">YOUR REFERENCE</span>
            <span className="font-display text-2xl text-white mt-1 tracking-wide">{reference}</span>
          </div>
        )}
        <div className="mt-6">
          <Button variant="outline" onClick={() => { setDone(false); setReference(null); }}>Submit another team</Button>
        </div>
      </motion.div>
    );
  }

  return (
    <form id={id} onSubmit={onSubmit} className="glass rounded-2xl p-6 sm:p-10 max-w-3xl mx-auto space-y-6">
      <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 sm:p-5 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="text-white font-semibold text-sm sm:text-base">Event full — pre-registration is temporarily closed</p>
          <p className="text-white/70 text-sm mt-1">
            Winner's Cup 2027 has reached capacity. Any form submitted from here will place your team on the
            waitlist, and we'll reach out if a spot becomes available.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">

        <Field label="Team Name" error={errors.team_name}>
          <Input className={inputClass} value={values.team_name} onChange={set("team_name")} placeholder="Winner's Elite" />
        </Field>
        <Field label="Team USAG Number" error={errors.team_usag_no}>
          <Input className={inputClass} value={values.team_usag_no} onChange={set("team_usag_no")} placeholder="123456" />
        </Field>
        <Field label="Club" error={errors.club}>
          <Input className={inputClass} value={values.club} onChange={set("club")} placeholder="Winner's Gymnastics" />
        </Field>
        <Field label="State" error={errors.state}>
          <Input className={inputClass} value={values.state} onChange={set("state")} placeholder="CA" />
        </Field>
        <Field label="Contact Name" error={errors.contact_name}>
          <Input className={inputClass} value={values.contact_name} onChange={set("contact_name")} placeholder="Jane Doe" />
        </Field>
        <Field label="Email" error={errors.email}>
          <Input className={inputClass} type="email" value={values.email} onChange={set("email")} placeholder="coach@club.com" />
        </Field>
        <Field label="Phone" error={errors.phone}>
          <Input className={inputClass} type="tel" value={values.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
        <Field label="# Compulsory" error={errors.num_compulsory}>
          <Input className={inputClass} type="number" min={0} value={values.num_compulsory} onChange={set("num_compulsory")} />
        </Field>
        <Field label="# Xcel" error={errors.num_xcel}>
          <Input className={inputClass} type="number" min={0} value={values.num_xcel} onChange={set("num_xcel")} />
        </Field>
        <Field label="# Optionals" error={errors.num_optionals}>
          <Input className={inputClass} type="number" min={0} value={values.num_optionals} onChange={set("num_optionals")} />
        </Field>
      </div>

      <div className="pt-2">
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <div>
            <Label className="text-white/80 block">Levels Attending</Label>
            <p className="text-white/40 text-xs mt-1">
              {levels.length > 0
                ? `${levels.length} selected`
                : "Tap to toggle the levels your team will compete"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLevels([...LEVELS])}
              className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setLevels([])}
              className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition"
              disabled={levels.length === 0}
            >
              Clear
            </button>
          </div>
        </div>

        <LevelGroup
          title="Compulsory & Optional"
          subtitle="Levels 2 – 10"
          items={NUM_LEVELS}
          selected={levels}
          onToggle={toggleLevel}
          onGroupAction={(action) =>
            setLevels((curr) =>
              action === "all"
                ? Array.from(new Set([...curr, ...NUM_LEVELS]))
                : curr.filter((l) => !NUM_LEVELS.includes(l)),
            )
          }
        />

        <div className="h-3" />

        <LevelGroup
          title="Xcel"
          subtitle="XB · XS · XG · XP · XD · XSA"
          items={X_LEVELS}
          selected={levels}
          onToggle={toggleLevel}
          onGroupAction={(action) =>
            setLevels((curr) =>
              action === "all"
                ? Array.from(new Set([...curr, ...X_LEVELS]))
                : curr.filter((l) => !X_LEVELS.includes(l)),
            )
          }
        />

        {levels.length > 0 && (
          <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-3">
            <div className="text-[11px] uppercase tracking-wider text-primary/80 font-semibold mb-2">
              Selection summary
            </div>
            <div className="flex flex-wrap gap-1.5">
              {LEVELS.filter((l) => levels.includes(l)).map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center gap-1 rounded-md bg-primary/15 text-primary text-xs font-medium px-2 py-0.5"
                >
                  {labelFor(l)}
                </span>
              ))}
            </div>
          </div>
        )}

        {errors.levels && <p className="text-sm text-destructive mt-2">{errors.levels}</p>}
      </div>

      <button
        type="button"
        onClick={() => setPriority2028((v) => !v)}
        aria-pressed={priority2028}
        className={`w-full flex items-start gap-3 text-left rounded-xl border p-4 transition ${
          priority2028
            ? "border-primary bg-primary/10"
            : "border-white/10 bg-white/[0.03] hover:border-white/25"
        }`}
      >
        <span
          className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-[5px] border shrink-0 transition ${
            priority2028 ? "border-primary bg-primary text-primary-foreground" : "border-white/25"
          }`}
          aria-hidden
        >
          {priority2028 && <Check className="w-3.5 h-3.5" />}
        </span>
        <span>
          <span className="block text-sm font-semibold text-white">
            Add priority registration for 2028
          </span>
          <span className="block text-xs text-white/60 mt-1">
            Notify me first when Winner's Cup 2028 details and registration open.
          </span>
        </span>
      </button>

      <Button type="submit" disabled={submitting} className="w-full bg-gradient-coral text-white font-bold h-12 text-base rounded-full shadow-glow hover:opacity-95">
        {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Join the Waitlist"}
      </Button>
      <p className="text-center text-white/40 text-xs">By submitting, you agree to be contacted with event updates.</p>

    </form>
  );
};

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label className="text-white/80">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

const LevelGroup = ({
  title,
  subtitle,
  items,
  selected,
  onToggle,
  onGroupAction,
}: {
  title: string;
  subtitle: string;
  items: readonly string[];
  selected: string[];
  onToggle: (lvl: string) => void;
  onGroupAction: (action: "all" | "clear") => void;
}) => {
  const groupSelected = items.filter((i) => selected.includes(i));
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 sm:p-4">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{title}</div>
          <div className="text-[11px] text-white/40 mt-0.5">{subtitle}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/50">
            {groupSelected.length}/{items.length}
          </span>
          <button
            type="button"
            onClick={() => onGroupAction(groupSelected.length === items.length ? "clear" : "all")}
            className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition"
          >
            {groupSelected.length === items.length ? "Clear" : "All"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {items.map((lvl) => {
          const active = selected.includes(lvl);
          return (
            <button
              key={lvl}
              type="button"
              onClick={() => onToggle(lvl)}
              aria-pressed={active}
              className={`group relative flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all ${
                active
                  ? "border-primary bg-primary/15 text-primary shadow-[0_0_0_1px_hsl(var(--primary)/0.5)]"
                  : "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/20 hover:text-white"
              }`}
            >
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-[4px] border transition ${
                  active ? "border-primary bg-primary text-primary-foreground" : "border-white/20 bg-transparent"
                }`}
                aria-hidden
              >
                {active && <Check className="w-3 h-3" />}
              </span>
              <span>{labelFor(lvl)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PreRegistrationForm;